import { Box, Card, CardContent, Skeleton, Tooltip, Typography } from "@mui/material";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { EnqueueSnackbar } from "notistack";
import AlertMessages from "common/Alert/AlertMessages";
import { Controller, useForm } from "react-hook-form";
import DynamicSelect from "common/Select/Select";
import { GetTopConsumedPartsResponse } from "subModule/src/services/analytics/interface";
import { useGetTopConsumedPartsService } from "subModule/src/services/analytics/useAnalytics";
import { InfiniteData } from "@tanstack/react-query";
import {
  dashboardWidgetHeaderSx,
  dashboardWidgetFiltersSx,
  dashboardWidgetTitleRowSx,
} from "components/Analytics/dashboardWidgetLayout";
interface MostConsumedPartsProps {
    themes: any;
    stores: { value: number; label: string }[];
    enqueueSnackbar: EnqueueSnackbar;
    storesLoaded: boolean;
    handleDropdownActions: (typeName: string, action: 'searchIt' | 'loadNext', search?: string) => void;
    setDropdownPage: any;
    storesData: InfiniteData<any, unknown> | undefined;
    searchLoading: string | null;
    dropdownPage: number;
    dropdownSearchTerm: any;
    setDropdownSearchTerm: any;
    globalFilters: { storeId: any; allStore?: boolean; range: any };
    globalFilterApplied: boolean;
    setGlobalFilterApplied: (value: boolean) => void;
}
const BAR_COLORS = [
    { bar: '#2F80ED', track: '#EEF2FF' }, // blue
    { bar: '#16A34A', track: '#ECFDF5' }, // green
    { bar: '#F97316', track: '#FFF7ED' }, // orange
    { bar: '#8B5CF6', track: '#F5F3FF' }, // purple
    { bar: '#C0841A', track: '#FFFBEB' }, // amber
    { bar: '#DB2777', track: '#FDF2F8' }, // pink
] as const;
export default function MostConsumedParts(props: MostConsumedPartsProps) {
    const { t, i18n } = useTranslation();
    const firstRender = useRef(true);
    const [data, setData] = useState<GetTopConsumedPartsResponse['data'] | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const { control, formState: { errors }, setValue, watch } = useForm<{ [key: string]: any }>({
        defaultValues: {
            range: { value: 'monthly', label: t('mtd') },
        },
    });
    const filters = useMemo(() => [
        { value: 'daily', label: t('today') },
        { value: 'yesterday', label: t('yesterday') },
        { value: 'weekly', label: t('wtd') },
        { value: 'monthly', label: t('mtd') },
        { value: 'last_month', label: t('last_month') },
        { value: 'yearly', label: t('ytd') },
    ], [i18n.language]);
    const values = watch(['storeId', 'range']);
    const selectedStoreValue = Number(values[0]?.value ?? values[0]);
    const isAllStoreSelected = selectedStoreValue === 0 || props.globalFilters.allStore === true;
    const { refetch: apiRefetch } = useGetTopConsumedPartsService({
        storeId: isAllStoreSelected ? null : (values[0]?.value || null),
        allStore: isAllStoreSelected,
        range: values[1]?.value || null,
    });
    const compactSelectSx = {
        '& .css-7896aj-control, & .css-1xiok1u-control': {
            minHeight: 'auto',
            fontSize: '9px',
            height: 'auto',
            overflow: 'hidden',
            '& .css-yzr845-indicatorContainer, & .css-1rr3n57-indicatorContainer, & .css-1asjhht-indicatorContainer, & .css-5s13ue-indicatorContainer': {
                background: '#fff',
                padding: 0,
                width: '15px',
                height: '15px',
            },
            '& .css-pogzpp-loadingIndicator, & .css-o9ehiq-loadingIndicator': { position: 'absolute', right: 0 },
            border: 0,
        },
    };
    useEffect(() => {
        if (firstRender.current && props.storesLoaded === true) {
            firstRender.current = false;
            const defaultStore = (props?.stores?.length > 1 ? props?.stores?.[1] : props?.stores?.[0]) || null;
            setValue('storeId', defaultStore);
            setValue('range', props.globalFilters.range);
            setTimeout(() => fetchData(), 50);
        }
    }, [props.stores, props.storesLoaded]);
    useEffect(() => {
        const currentRange = watch('range');
        const updated = filters.find(f => f.value === currentRange?.value);
        if (updated) setValue('range', updated);
    }, [i18n.language, filters]);
    useEffect(() => {
        if (props.globalFilterApplied === true) {
            setValue('storeId', props.globalFilters.storeId);
            setValue('range', props.globalFilters.range);
            setTimeout(() => fetchData(), 100);
        }
    }, [props.globalFilterApplied]);
    const fetchData = async () => {
        setLoading(true);
        await apiRefetch().then((res: GetTopConsumedPartsResponse | unknown | any) => {
            if (res?.data?.success) {
                setData(res?.data?.data || null);
            } else {
                props.enqueueSnackbar(<AlertMessages text={res?.data?.message || "Something went wrong"} />, { variant: "error" });
            }
        }).catch((err) => {
            console.error("===", err);
        }).finally(() => {
            setLoading(false);
            if (props.globalFilterApplied === true) props.setGlobalFilterApplied(false);
        });
    };
    const parts = useMemo(() => {
        const list = (data?.parts || []).slice();
        // Requirement: highest unitsConsumed & progressPercent on top
        // Stable fallback by rank (lower rank number usually means higher priority)
        return list.sort((a, b) =>
            (b.unitsConsumed - a.unitsConsumed) ||
            (b.progressPercent - a.progressPercent) ||
            (a.rank - b.rank)
        );
    }, [data]);
    return (
        <Card sx={{ height: '285px', boxShadow: 'none', borderRadius: '8px', border: '1px solid #E5E7EB', '& .MuiCardContent-root': { padding: '16px !important' } }}>
            <CardContent>
                <Box sx={dashboardWidgetHeaderSx}>
                    <Box sx={dashboardWidgetTitleRowSx}>
                    <Typography variant="h1" gutterBottom className="font-Saira text-start mb-0 mt-[5px]" sx={{ color: props.themes?.primary, fontSize: { xs: 12, sm: 14 }, fontWeight: { xs: 600, sm: 600 }, textAlign: "left" }}>
                        {t('mostConsumedSpareParts')}
                    </Typography>
                    </Box>
                    <Box sx={dashboardWidgetFiltersSx}>
                        <Controller
                            name="storeId"
                            control={control}
                            defaultValue={null}
                            rules={{ required: t("This field is required.") }}
                            render={({ field }) => (
                                <Box sx={compactSelectSx}>
                                    <DynamicSelect
                                        label={''}
                                        {...field}
                                        options={props?.stores || []}
                                        isDisabled={loading}
                                        onChange={(selectedOption: any) => {
                                            field.onChange(selectedOption);
                                            setTimeout(() => fetchData(), 50);
                                        }}
                                        error={errors.storeId?.message as string}
                                        ddWidth="30px"
                                        ddHeight="30px"
                                        defaultPlaceholder={t('store')}
                                        isLoading={props.searchLoading === 'mostConsumedParts'}
                                        onInputChange={(search: string, { action }: { action: string }) => {
                                            if (action === 'input-change' ||
                                                (action !== 'set-value' && action !== 'menu-close' && action !== 'input-blur')
                                            ) props.handleDropdownActions("mostConsumedParts", 'searchIt', search);
                                            else if (action === 'input-blur' || action === 'menu-close') {
                                                if (props.dropdownSearchTerm || props.dropdownPage > 1) props.handleDropdownActions("mostConsumedParts", 'searchIt', '');
                                                else props.setDropdownSearchTerm('');
                                                props.setDropdownPage(1);
                                            }
                                        }}
                                        onMenuScrollToBottom={() => { if (!(props.storesData?.pages[0]?.data as any)?.last) props.handleDropdownActions("mostConsumedParts", 'loadNext'); }}
                                        page={props.dropdownPage}
                                        isSearchable
                                    />
                                </Box>
                            )}
                        />
                        <Controller
                            name="range"
                            control={control}
                            defaultValue={null}
                            rules={{ required: t("This field is required.") }}
                            render={({ field }) => (
                                <Box sx={compactSelectSx}>
                                    <DynamicSelect
                                        label={''}
                                        {...field}
                                        options={filters}
                                        onChange={(selectedOption: any) => {
                                            field.onChange(selectedOption);
                                            setTimeout(() => fetchData(), 50);
                                        }}
                                        error={errors.range?.message as string}
                                        ddWidth="30px"
                                        ddHeight="30px"
                                        isDisabled={loading}
                                    />
                                </Box>
                            )}
                        />
                    </Box>
                </Box>
                {loading ? (
                    <>
                        <Skeleton animation="pulse" sx={{ backgroundColor: props?.themes?.secondary, marginTop: '5px' }} />
                        <Skeleton animation="pulse" sx={{ backgroundColor: props?.themes?.secondary }} />
                        <Skeleton animation="pulse" sx={{ backgroundColor: props?.themes?.secondary }} />
                        <Skeleton animation="pulse" sx={{ backgroundColor: props?.themes?.secondary }} />
                        <Skeleton animation="pulse" sx={{ backgroundColor: props?.themes?.secondary }} />
                        <Skeleton animation="pulse" sx={{ backgroundColor: props?.themes?.secondary }} />
                    </>
                ) : (<Box>
                    <Box className="flex items-center gap-1 mb-[10px]">
                        <Typography variant="h4" className="font-Saira text-start text-[14px]">{t('totalUnitsConsumed') + ':'}</Typography>
                        <span className="font-Saira text-[15px] font-bold" style={{ color: props.themes.primary }}>
                            {new Intl.NumberFormat('en', {
                                notation: 'compact',
                                compactDisplay: 'short',
                                maximumFractionDigits: 2,
                            }).format(data?.totalUnitsConsumed || 0)}
                        </span>
                    </Box>
                    <Box>
                        {(parts.length === 0 || !data) ? (
                            <Box className="flex justify-center flex-col">
                                <Typography className="font-Saira text-center italic font-semibold mt-[67px]" sx={{ color: props.themes.primary }}>
                                    {t('noRecordFound')}
                                </Typography>
                            </Box>
                        ) : (
                            <Box className="max-h-[185px] overflow-y-auto pr-[6px]">
                                {parts.map((item, index) => {
                                    const color = BAR_COLORS[index % BAR_COLORS.length];
                                    const progress = Math.max(0, Math.min(100, Number(item.progressPercent || 0)));
                                    return (
                                        <Box key={`${item.partId}-${index}`} className="mb-3">
                                            <Box className="flex items-center justify-between gap-3 mb-1">
                                                <Tooltip followCursor title={item.partName}>
                                                    <Typography className="font-Saira text-[12px] font-medium text-[#171826] truncate max-w-[240px]">
                                                        {item.partName}
                                                    </Typography>
                                                </Tooltip>
                                                <Typography className="font-Saira text-[13px] font-semibold text-[#171826]">
                                                    {(item.unitsConsumed ?? 0).toLocaleString()}
                                                </Typography>
                                            </Box>
                                            <Box
                                                sx={{
                                                    height: 10,
                                                    borderRadius: '999px',
                                                    backgroundColor: '#F3F4F6',
                                                    overflow: 'hidden',
                                                }}
                                            >
                                                <Box
                                                    sx={{
                                                        height: '100%',
                                                        width: `${progress}%`,
                                                        backgroundColor: color.bar,
                                                        borderRadius: '999px',
                                                        transition: 'width 0.3s ease',
                                                    }}
                                                />
                                            </Box>
                                        </Box>
                                    );
                                })}
                            </Box>
                        )}
                    </Box>
                </Box>)}
            </CardContent>
        </Card>
    );
}
